import {
  useLLMNeighborhoodInsights,
  useLLGeographies,
  useLLValueDrivers,
  useLLSchools,
  useLLMarketStatistics,
  useLocationScores,
  useLLPointsOfInterest,
  useLocationCoordinates,
  useLLDemographics,
  useLLSimilarNeighborhoods,
} from '@ltvco/refresh-lib/hooks';
import { Box, Text, styled } from '@ltvco/refresh-lib/theme';
import { Feedback } from '@ltvco/refresh-lib/v1';
import {
  Characteristics,
  MarketTrends,
  NearbySchools,
  NeighborhoodMapInsights,
  NeighborhoodProfile,
  PropertyValueDrivers,
  Demographics,
  SimilarNeighborhoodsSection,
  LoadingDots,
} from '@ltvco/refresh-lib/v2';
import { useEffect, useMemo, useState } from 'react';

import { constants } from 'appConstants';

const LoadingContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '180px',
  background: theme.palette.background.paper,
  borderRadius: theme.spacing(3),
  boxShadow: theme.shadows[1],
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  paddingTop: theme.spacing(6),
  paddingBottom: theme.spacing(6),
}));

const ErrorContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '120px',
  background: theme.palette.error.light,
  borderRadius: theme.spacing(3),
  boxShadow: theme.shadows[1],
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(4),
}));

const DisclaimerContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2.5),
  background: theme.palette.background.paper,
  borderRadius: theme.spacing(1.5),
  border: `1px solid ${theme.palette.divider}`,
}));

interface NeighborhoodReportContentProps {
  address: string;
  propertyReportMode?: boolean;
}

const NeighborhoodReportContent = ({
  address,
  propertyReportMode = false,
}: NeighborhoodReportContentProps) => {
  /////////////////////////////
  // States
  /////////////////////////////
  const [isSearching, setIsSearching] = useState(Boolean(address));
  const [coordinates, setCoordinates] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);

  const hasValidCoordinates =
    coordinates?.latitude !== null &&
    coordinates?.latitude !== undefined &&
    coordinates?.longitude !== null &&
    coordinates?.longitude !== undefined;

  /////////////////////////////
  // Hooks
  /////////////////////////////
  const {
    links: { neighborhoodInsightsFeedbackLink },
  } = constants;

  const {
    data: locationCoordinates,
    isLoading: isLoadingCoordinates,
    isError: isCoordinatesError,
  } = useLocationCoordinates(address);

  const { data: schoolsData, isLoading: isLoadingSchools } = useLLSchools({
    lat: coordinates?.latitude,
    lng: coordinates?.longitude,
  });

  const {
    reportObject,
    isLoading: isLoadingInsights,
    isError,
  } = useLLMNeighborhoodInsights({
    location: address,
  });

  const summary = reportObject?.neighborhoodReport?.summary?.[0] ?? '';

  const { data: geographiesData } = useLLGeographies({
    lat: coordinates?.latitude,
    lng: coordinates?.longitude,
  });

  const geogId = useMemo(() => {
    if (!geographiesData?.data?.geographies) {
      return '';
    }
    const geographies = geographiesData.data.geographies;
    const geogIds = Object.keys(geographies);

    // Try level 10 (neighborhood) first, then fall back to other levels
    const levelPriority = [10, 20, 30, 32, 35];
    for (const level of levelPriority) {
      const foundId = geogIds.find((id) => geographies[id].level === level);
      if (foundId) {
        return foundId;
      }
    }

    // If no preferred level found, use the first available
    return geogIds[0] || '';
  }, [geographiesData]);

  const hasError = isError || reportObject?.isSearchError || isCoordinatesError;

  const { data: valueDriversData, isLoading: isLoadingValueDrivers } =
    useLLValueDrivers({
      geog_id: geogId,
    });

  const { data: marketStatsData, isLoading: isLoadingMarketStats } =
    useLLMarketStatistics({
      geog_id: geogId,
    });

  const { data: locationScoresData, isLoading: isLoadingLocationScores } =
    useLocationScores({
      lat: coordinates?.latitude,
      lng: coordinates?.longitude,
    });

  const { data: poisData, isLoading: isLoadingPOIs } = useLLPointsOfInterest({
    lat: coordinates?.latitude,
    lng: coordinates?.longitude,
    radius: 2000,
    limit: 100,
  });

  const { data: demographicsData, isLoading: isLoadingDemographics } =
    useLLDemographics({
      geog_id: geogId,
    });

  const {
    data: similarNeighborhoodsData,
    isLoading: isLoadingSimilarNeighborhoods,
  } = useLLSimilarNeighborhoods({
    geog_id: geogId,
    limit: 3,
  });

  const similarNeighborhoodIds = useMemo(() => {
    return similarNeighborhoodsData?.data?.most_similar?.map((n) => n.id) || [];
  }, [similarNeighborhoodsData]);

  const { data: firstNeighborhoodScores, isLoading: isLoadingFirstScores } =
    useLocationScores({
      geography_ids: similarNeighborhoodIds[0] || '',
    });
  const { data: secondNeighborhoodScores, isLoading: isLoadingSecondScores } =
    useLocationScores({
      geography_ids: similarNeighborhoodIds[1] || '',
    });
  const { data: thirdNeighborhoodScores, isLoading: isLoadingThirdScores } =
    useLocationScores({
      geography_ids: similarNeighborhoodIds[2] || '',
    });

  /////////////////////////////
  // Effects
  /////////////////////////////
  // Begin "searching" UI as soon as the address changes; clear stale coordinates
  // so dependent hooks don't fetch with the previous location's lat/lng.
  useEffect(() => {
    if (address) {
      setCoordinates(null);
      setIsSearching(true);
    }
  }, [address]);

  useEffect(() => {
    if (!address || isLoadingCoordinates) {
      return;
    }

    if (isCoordinatesError || !locationCoordinates) {
      setIsSearching(false);
      return;
    }

    setCoordinates({
      latitude: locationCoordinates.latitude,
      longitude: locationCoordinates.longitude,
    });
  }, [address, isLoadingCoordinates, isCoordinatesError, locationCoordinates]);

  useEffect(() => {
    if (!address) {
      return;
    }
    const anyLoading =
      isLoadingSchools ||
      isLoadingInsights ||
      isLoadingMarketStats ||
      isLoadingLocationScores ||
      isLoadingDemographics ||
      isLoadingValueDrivers ||
      isLoadingPOIs ||
      isLoadingSimilarNeighborhoods;
    if (!anyLoading) {
      setIsSearching(false);
    }
  }, [
    address,
    isLoadingSchools,
    isLoadingInsights,
    isLoadingMarketStats,
    isLoadingLocationScores,
    isLoadingDemographics,
    isLoadingValueDrivers,
    isLoadingPOIs,
    isLoadingSimilarNeighborhoods,
  ]);

  /////////////////////////////
  // Render
  /////////////////////////////
  return (
    <>
      {isSearching && !hasError && (
        <Box mt={6} mb={4}>
          <LoadingContainer>
            <Text variant="h4" color="primary" mb={2} fontWeight={700}>
              Generating Neighborhood Insights for {address}
            </Text>
            <LoadingDots count={6} />
          </LoadingContainer>
        </Box>
      )}
      {hasError && (
        <Box mt={6} mb={4}>
          <ErrorContainer>
            <Text variant="h4" color="error" mb={2} fontWeight={700}>
              Unable to generate Neighborhood Insights
            </Text>
            <Text variant="body1" color="error">
              {reportObject?.error ?? 'An unexpected error occurred.'}
            </Text>
          </ErrorContainer>
        </Box>
      )}
      {address && !isSearching && !hasError && (
        <Box
          sx={{
            borderRadius: 2,
            backgroundColor: '#F8F8F8',
            p: '29px 22px',
            mt: propertyReportMode ? 0 : 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <NeighborhoodProfile
            neighborhoodName={address}
            summary={summary}
            isLoading={isLoadingInsights}
          />
          <MarketTrends
            marketData={marketStatsData?.data || null}
            isLoading={isLoadingMarketStats}
          />
          <Characteristics
            scoresData={locationScoresData?.data || null}
            isLoading={isLoadingLocationScores}
          />
          <Demographics
            demographicsData={demographicsData || null}
            isLoading={isLoadingDemographics}
          />
          <PropertyValueDrivers
            valueDrivers={valueDriversData?.data?.value_drivers || []}
            isLoading={isLoadingValueDrivers}
          />
          {hasValidCoordinates && coordinates && (
            <NeighborhoodMapInsights
              poisData={poisData || null}
              lat={coordinates.latitude}
              lng={coordinates.longitude}
              isPoisLoading={isLoadingPOIs}
              address={address}
            />
          )}

          <NearbySchools
            schools={[
              ...(schoolsData?.data?.primary_schools || []),
              ...(schoolsData?.data?.high_schools || []),
            ]}
            isLoading={isLoadingSchools}
          />
          <SimilarNeighborhoodsSection
            similarNeighborhoodsData={similarNeighborhoodsData?.data || null}
            neighborhoodScores={[
              firstNeighborhoodScores?.data || null,
              secondNeighborhoodScores?.data || null,
              thirdNeighborhoodScores?.data || null,
            ]}
            isLoadingScores={[
              isLoadingFirstScores,
              isLoadingSecondScores,
              isLoadingThirdScores,
            ]}
            isLoading={isLoadingSimilarNeighborhoods}
          />

          <DisclaimerContainer>
            <Text
              variant="body2"
              color="text.secondary"
              sx={{
                fontSize: '0.875rem',
                lineHeight: 1.5,
                fontStyle: 'italic',
              }}
            >
              <strong>Disclaimer:</strong> Smart Insights includes AI-generated
              content, which may contain errors, inconsistencies, and/or
              outdated information. Such content is provided for informational
              purposes only and should not be relied upon for any specific
              purpose without verification of its accuracy or completeness.
            </Text>
          </DisclaimerContainer>
          <Feedback
            lightTheme
            feedbackFormUrl={neighborhoodInsightsFeedbackLink}
          />
        </Box>
      )}
    </>
  );
};

export default NeighborhoodReportContent;
